import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../firebaseUtils';
import { auth } from '../firebaseConfig';
import { useAuthState } from 'react-firebase-hooks/auth';
import Sidebar from './Sidebar';
import TileMenu from './TileMenu';
import './Projects.css';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [user] = useAuthState(auth);
  const [role, setRole] = useState('engineer');
  const [userName, setUserName] = useState('');
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectNameDraft, setProjectNameDraft] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProjects = async () => {
      if (!user) return;

      const q = query(
        collection(db, 'projects'),
        where('members', 'array-contains', user.uid)
      );
      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => (a.name || '').localeCompare(b.name || '')); // ✅ sort by name
      setProjects(data);
    };

    fetchProjects();
  }, [user]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            if (data.role) setRole(data.role);
            if (data.name) setUserName(data.name);
          }
        } catch (error) {
          console.error('Error fetching user details:', error);
        }
      }
    };

    fetchUserDetails();
  }, [user]);

  const createNewProject = async () => {
    const name = prompt('Enter a project name:');
    if (!name || !user) return;

    try {
      const docRef = await addDoc(collection(db, 'projects'), {
        name: name,
        ownerId: user.uid,
        members: [user.uid],
        createdAt: new Date(),
        captions: {}
      });

      navigate(`/project/${docRef.id}`);
    } catch (error) {
      console.error("❌ Project creation failed:", error);
      alert("Failed to create project. Please check permissions and try again.");
    }
  };

  const startEditingProjectName = (project) => {
    setProjectNameDraft(project.name || '');
    setEditingProjectId(project.id);
  };

  const commitProjectRename = async (projectId) => {
    const trimmed = projectNameDraft.trim();
    const current = projects.find(p => p.id === projectId);
    setEditingProjectId(null);

    if (!trimmed) return; // reject empty/whitespace-only name
    if ((current?.name || '') === trimmed) return; // unchanged — skip the write

    const previousName = current?.name;
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, name: trimmed } : p));
    try {
      await updateDoc(doc(db, 'projects', projectId), { name: trimmed });
    } catch (err) {
      console.error('Error renaming project:', err);
      setProjects(prev => prev.map(p => p.id === projectId ? { ...p, name: previousName } : p));
      alert('Could not rename project.');
    }
  };

  return (
    <div className="ProjectsPage">
      <header>Projects</header>

      <Sidebar
        role={role}
        buttons={[
          {
            label: '➕ New Project',
            action: createNewProject,
            special: true
          }
        ]}
      />

      <div
        className="main-content"
        style={{
          marginLeft: '200px',
          padding: '0.25rem 2rem 2rem 0px',
        }}
      >
        {userName && (
          <h2 style={{ fontSize: '1.5rem', marginTop: '0rem' }}>
            Welcome {userName}
          </h2>
        )}

        <div className="project-grid">
          {projects.map(project => (
            <div
              key={project.id}
              className="project-card"
              onClick={() => navigate(`/project/${project.id}`)}
            >
              <TileMenu
                items={[
                  { key: 'rename', label: 'Rename', onSelect: () => startEditingProjectName(project) }
                ]}
                isOpen={openMenuId === project.id}
                onOpen={() => setOpenMenuId(project.id)}
                onClose={() => setOpenMenuId(null)}
              />
              <div className="thumbnail-placeholder">📁</div>
              {editingProjectId === project.id ? (
                <input
                  type="text"
                  className="project-name-input"
                  autoFocus
                  value={projectNameDraft}
                  onChange={(e) => setProjectNameDraft(e.target.value)}
                  onBlur={() => commitProjectRename(project.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitProjectRename(project.id);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingProjectId(null);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="project-name">{project.name || `Project ${project.id}`}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Projects;
